import React, { useState, useEffect } from 'react';
import { Routes, Route, Link, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import { adminAPI } from '../services/api';
import './Admin.css';

function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await adminAPI.getStats();
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
      toast.error('Failed to load admin stats');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="spinner" />;
  }

  return (
    <div className="admin-dashboard">
      <h2>Dashboard Overview</h2>
      <div className="stats-grid">
        <div className="stat-card card">
          <h3>Users</h3>
          <div className="stat-value">{stats?.users?.total || 0}</div>
          <div className="stat-label">{stats?.users?.active || 0} active</div>
        </div>
        <div className="stat-card card">
          <h3>Generations</h3>
          <div className="stat-value">{stats?.generations?.total || 0}</div>
          <div className="stat-label">
            {stats?.generations?.completed || 0} completed
          </div>
        </div>
        <div className="stat-card card">
          <h3>Revenue</h3>
          <div className="stat-value">
            ${stats?.revenue?.total_credits_purchased?.toFixed(2) || '0.00'}
          </div>
          <div className="stat-label">credits purchased</div>
        </div>
        <div className="stat-card card">
          <h3>Profit</h3>
          <div className="stat-value">
            ${stats?.revenue?.profit?.toFixed(2) || '0.00'}
          </div>
          <div className="stat-label">
            after ${stats?.revenue?.runware_costs?.toFixed(2) || '0.00'} API costs
          </div>
        </div>
        <div className="stat-card card">
          <h3>Memberships</h3>
          <div className="stat-value">{stats?.memberships?.active || 0}</div>
          <div className="stat-label">active subscriptions</div>
        </div>
      </div>
    </div>
  );
}

function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [creditAmount, setCreditAmount] = useState('');
  const [creditDescription, setCreditDescription] = useState('');

  useEffect(() => {
    fetchUsers();
  }, [page, search]);

  const fetchUsers = async () => {
    try {
      const response = await adminAPI.listUsers(page, 20, search);
      setUsers(response.data.users);
      setTotal(response.data.total);
    } catch (error) {
      console.error('Failed to fetch users:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddCredits = async () => {
    if (!creditAmount || parseFloat(creditAmount) <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }

    try {
      await adminAPI.addCredits(
        selectedUser.id,
        parseFloat(creditAmount),
        creditDescription || 'Admin credit grant'
      );
      toast.success('Credits added successfully');
      setSelectedUser(null);
      setCreditAmount('');
      setCreditDescription('');
      fetchUsers();
    } catch (error) {
      toast.error('Failed to add credits');
    }
  };

  const handleToggleAdmin = async (user) => {
    try {
      await adminAPI.updateUser(user.id, { is_admin: !user.is_admin });
      toast.success(`Admin status updated`);
      fetchUsers();
    } catch (error) {
      toast.error('Failed to update user');
    }
  };

  const handleToggleActive = async (user) => {
    try {
      await adminAPI.updateUser(user.id, { is_active: !user.is_active });
      toast.success(`User ${user.is_active ? 'deactivated' : 'activated'}`);
      fetchUsers();
    } catch (error) {
      toast.error('Failed to update user');
    }
  };

  return (
    <div className="admin-users">
      <h2>Users</h2>

      <div className="admin-toolbar">
        <input
          type="text"
          className="input"
          placeholder="Search users..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      {loading ? (
        <div className="spinner" />
      ) : (
        <>
          <div className="table-container card">
            <table className="table">
              <thead>
                <tr>
                  <th>User</th>
                  <th>Email</th>
                  <th>Credits</th>
                  <th>Role</th>
                  <th>Status</th>
                  <th>Joined</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map((user) => (
                  <tr key={user.id}>
                    <td>
                      <div className="user-cell">
                        {user.picture ? (
                          <img
                            src={user.picture}
                            alt={user.name}
                            className="user-avatar-sm"
                          />
                        ) : (
                          <div className="user-avatar-placeholder-sm">
                            {user.name?.[0] || user.email[0]}
                          </div>
                        )}
                        <span>{user.name || 'N/A'}</span>
                      </div>
                    </td>
                    <td>{user.email}</td>
                    <td>{user.credits?.toFixed(0)}</td>
                    <td>
                      <span
                        className={`badge ${
                          user.is_admin ? 'badge-warning' : 'badge-primary'
                        }`}
                      >
                        {user.is_admin ? 'Admin' : 'User'}
                      </span>
                    </td>
                    <td>
                      <span
                        className={`badge ${
                          user.is_active ? 'badge-success' : 'badge-danger'
                        }`}
                      >
                        {user.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td>{new Date(user.created_at).toLocaleDateString()}</td>
                    <td>
                      <div className="action-buttons">
                        <button
                          className="btn btn-sm btn-secondary"
                          onClick={() => setSelectedUser(user)}
                        >
                          Add Credits
                        </button>
                        <button
                          className="btn btn-sm btn-secondary"
                          onClick={() => handleToggleAdmin(user)}
                        >
                          {user.is_admin ? 'Remove Admin' : 'Make Admin'}
                        </button>
                        <button
                          className={`btn btn-sm ${
                            user.is_active ? 'btn-danger' : 'btn-success'
                          }`}
                          onClick={() => handleToggleActive(user)}
                        >
                          {user.is_active ? 'Deactivate' : 'Activate'}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="pagination">
            <button
              className="btn btn-secondary btn-sm"
              disabled={page === 1}
              onClick={() => setPage(page - 1)}
            >
              Previous
            </button>
            <span>
              Page {page} of {Math.ceil(total / 20)}
            </span>
            <button
              className="btn btn-secondary btn-sm"
              disabled={page >= Math.ceil(total / 20)}
              onClick={() => setPage(page + 1)}
            >
              Next
            </button>
          </div>
        </>
      )}

      {/* Add Credits Modal */}
      {selectedUser && (
        <div className="modal-overlay" onClick={() => setSelectedUser(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">Add Credits</h3>
              <button
                className="modal-close"
                onClick={() => setSelectedUser(null)}
              >
                ×
              </button>
            </div>
            <p>Adding credits to: {selectedUser.email}</p>
            <p>Current balance: {selectedUser.credits?.toFixed(0)} credits</p>
            <div className="form-group">
              <label className="label">Amount</label>
              <input
                type="number"
                className="input"
                value={creditAmount}
                onChange={(e) => setCreditAmount(e.target.value)}
                placeholder="Enter amount"
              />
            </div>
            <div className="form-group">
              <label className="label">Description (optional)</label>
              <input
                type="text"
                className="input"
                value={creditDescription}
                onChange={(e) => setCreditDescription(e.target.value)}
                placeholder="Reason for credit grant"
              />
            </div>
            <div className="modal-actions">
              <button
                className="btn btn-secondary"
                onClick={() => setSelectedUser(null)}
              >
                Cancel
              </button>
              <button className="btn btn-primary" onClick={handleAddCredits}>
                Add Credits
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function AdminGenerations() {
  const [generations, setGenerations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  useEffect(() => {
    fetchGenerations();
  }, [page]);

  const fetchGenerations = async () => {
    try {
      const response = await adminAPI.listGenerations(page, 20);
      setGenerations(response.data.generations);
      setTotalPages(response.data.pages);
    } catch (error) {
      console.error('Failed to fetch generations:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      completed: 'badge-success',
      processing: 'badge-warning',
      failed: 'badge-danger',
      pending: 'badge-primary',
    };
    return badges[status] || 'badge-primary';
  };

  return (
    <div className="admin-generations">
      <h2>Generations</h2>

      {loading ? (
        <div className="spinner" />
      ) : (
        <>
          <div className="table-container card">
            <table className="table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>User</th>
                  <th>Type</th>
                  <th>Material</th>
                  <th>Status</th>
                  <th>Cost</th>
                  <th>Created</th>
                </tr>
              </thead>
              <tbody>
                {generations.map((gen) => (
                  <tr key={gen.id}>
                    <td>#{gen.id}</td>
                    <td>{gen.user_id}</td>
                    <td className="capitalize">{gen.generation_type.replace('_', ' ')}</td>
                    <td className="capitalize">
                      {gen.material_type?.replace('_', ' ') || '-'}
                    </td>
                    <td>
                      <span className={`badge ${getStatusBadge(gen.status)}`}>
                        {gen.status}
                      </span>
                    </td>
                    <td>{gen.credits_used} credits</td>
                    <td>{new Date(gen.created_at).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="pagination">
            <button
              className="btn btn-secondary btn-sm"
              disabled={page === 1}
              onClick={() => setPage(page - 1)}
            >
              Previous
            </button>
            <span>Page {page} of {totalPages}</span>
            <button
              className="btn btn-secondary btn-sm"
              disabled={page >= totalPages}
              onClick={() => setPage(page + 1)}
            >
              Next
            </button>
          </div>
        </>
      )}
    </div>
  );
}

function AdminCreditPacks() {
  const [packs, setPacks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    credits: '',
    price: '',
    bonus_credits: '0',
    description: '',
  });

  useEffect(() => {
    fetchPacks();
  }, []);

  const fetchPacks = async () => {
    try {
      const response = await adminAPI.listCreditPacks();
      setPacks(response.data);
    } catch (error) {
      console.error('Failed to fetch credit packs:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await adminAPI.createCreditPack({
        ...formData,
        credits: parseInt(formData.credits),
        price: parseFloat(formData.price),
        bonus_credits: parseInt(formData.bonus_credits),
      });
      toast.success('Credit pack created');
      setShowForm(false);
      setFormData({
        name: '',
        credits: '',
        price: '',
        bonus_credits: '0',
        description: '',
      });
      fetchPacks();
    } catch (error) {
      toast.error('Failed to create credit pack');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Deactivate this credit pack?')) return;
    try {
      await adminAPI.deleteCreditPack(id);
      toast.success('Credit pack deactivated');
      fetchPacks();
    } catch (error) {
      toast.error('Failed to deactivate credit pack');
    }
  };

  return (
    <div className="admin-credit-packs">
      <div className="admin-header">
        <h2>Credit Packs</h2>
        <button className="btn btn-primary" onClick={() => setShowForm(true)}>
          Add Credit Pack
        </button>
      </div>

      {loading ? (
        <div className="spinner" />
      ) : (
        <div className="table-container card">
          <table className="table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Credits</th>
                <th>Bonus</th>
                <th>Price</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {packs.map((pack) => (
                <tr key={pack.id}>
                  <td>{pack.name}</td>
                  <td>{pack.credits}</td>
                  <td>{pack.bonus_credits}</td>
                  <td>${pack.price.toFixed(2)}</td>
                  <td>
                    <span
                      className={`badge ${
                        pack.is_active ? 'badge-success' : 'badge-danger'
                      }`}
                    >
                      {pack.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td>
                    {pack.is_active && (
                      <button
                        className="btn btn-sm btn-danger"
                        onClick={() => handleDelete(pack.id)}
                      >
                        Deactivate
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Add Pack Modal */}
      {showForm && (
        <div className="modal-overlay" onClick={() => setShowForm(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">Add Credit Pack</h3>
              <button className="modal-close" onClick={() => setShowForm(false)}>
                ×
              </button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label className="label">Name</label>
                <input
                  type="text"
                  className="input"
                  value={formData.name}
                  onChange={(e) =>
                    setFormData({ ...formData, name: e.target.value })
                  }
                  required
                />
              </div>
              <div className="form-group">
                <label className="label">Credits</label>
                <input
                  type="number"
                  className="input"
                  value={formData.credits}
                  onChange={(e) =>
                    setFormData({ ...formData, credits: e.target.value })
                  }
                  required
                />
              </div>
              <div className="form-group">
                <label className="label">Bonus Credits</label>
                <input
                  type="number"
                  className="input"
                  value={formData.bonus_credits}
                  onChange={(e) =>
                    setFormData({ ...formData, bonus_credits: e.target.value })
                  }
                />
              </div>
              <div className="form-group">
                <label className="label">Price ($)</label>
                <input
                  type="number"
                  step="0.01"
                  className="input"
                  value={formData.price}
                  onChange={(e) =>
                    setFormData({ ...formData, price: e.target.value })
                  }
                  required
                />
              </div>
              <div className="form-group">
                <label className="label">Description</label>
                <input
                  type="text"
                  className="input"
                  value={formData.description}
                  onChange={(e) =>
                    setFormData({ ...formData, description: e.target.value })
                  }
                />
              </div>
              <div className="modal-actions">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowForm(false)}
                >
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  Create Pack
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

function AdminMembershipPlans() {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPlans();
  }, []);

  const fetchPlans = async () => {
    try {
      const response = await adminAPI.listMembershipPlans();
      setPlans(response.data);
    } catch (error) {
      console.error('Failed to fetch membership plans:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="admin-membership-plans">
      <h2>Membership Plans</h2>

      {loading ? (
        <div className="spinner" />
      ) : (
        <div className="table-container card">
          <table className="table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Tier</th>
                <th>Monthly</th>
                <th>Yearly</th>
                <th>Credits/Month</th>
                <th>Bonus %</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {plans.map((plan) => (
                <tr key={plan.id}>
                  <td>{plan.name}</td>
                  <td className="capitalize">{plan.tier}</td>
                  <td>${plan.price_monthly.toFixed(2)}</td>
                  <td>${plan.price_yearly?.toFixed(2) || '-'}</td>
                  <td>{plan.credits_per_month}</td>
                  <td>{plan.bonus_credits_percentage}%</td>
                  <td>
                    <span
                      className={`badge ${
                        plan.is_active ? 'badge-success' : 'badge-danger'
                      }`}
                    >
                      {plan.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function AdminTransactions() {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [typeFilter, setTypeFilter] = useState('');

  useEffect(() => {
    fetchTransactions();
  }, [page, typeFilter]);

  const fetchTransactions = async () => {
    try {
      const response = await adminAPI.listTransactions(page, 20, typeFilter || null);
      setTransactions(response.data.transactions);
      setTotalPages(response.data.pages);
    } catch (error) {
      console.error('Failed to fetch transactions:', error);
    } finally {
      setLoading(false);
    }
  };

  const getTypeBadge = (type) => {
    const badges = {
      purchase: 'badge-success',
      membership_grant: 'badge-primary',
      admin_grant: 'badge-warning',
      generation_use: 'badge-danger',
      refund: 'badge-secondary',
      bonus: 'badge-info',
    };
    return badges[type] || 'badge-primary';
  };

  return (
    <div className="admin-transactions">
      <h2>Transactions</h2>

      <div className="admin-toolbar">
        <select
          className="input"
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value)}
          style={{ maxWidth: '200px' }}
        >
          <option value="">All Types</option>
          <option value="purchase">Purchases</option>
          <option value="membership_grant">Membership Grants</option>
          <option value="admin_grant">Admin Grants</option>
          <option value="generation_use">Generation Usage</option>
          <option value="refund">Refunds</option>
          <option value="bonus">Bonuses</option>
        </select>
      </div>

      {loading ? (
        <div className="spinner" />
      ) : (
        <>
          <div className="table-container card">
            <table className="table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>User</th>
                  <th>Type</th>
                  <th>Amount</th>
                  <th>Balance After</th>
                  <th>Description</th>
                  <th>Date</th>
                </tr>
              </thead>
              <tbody>
                {transactions.map((trans) => (
                  <tr key={trans.id}>
                    <td>#{trans.id}</td>
                    <td>{trans.user_email || `User #${trans.user_id}`}</td>
                    <td>
                      <span className={`badge ${getTypeBadge(trans.type)}`}>
                        {trans.type.replace('_', ' ')}
                      </span>
                    </td>
                    <td className={trans.amount >= 0 ? 'text-success' : 'text-danger'}>
                      {trans.amount >= 0 ? '+' : ''}{trans.amount}
                    </td>
                    <td>{trans.balance_after}</td>
                    <td>{trans.description || '-'}</td>
                    <td>{new Date(trans.created_at).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="pagination">
            <button
              className="btn btn-secondary btn-sm"
              disabled={page === 1}
              onClick={() => setPage(page - 1)}
            >
              Previous
            </button>
            <span>Page {page} of {totalPages}</span>
            <button
              className="btn btn-secondary btn-sm"
              disabled={page >= totalPages}
              onClick={() => setPage(page + 1)}
            >
              Next
            </button>
          </div>
        </>
      )}
    </div>
  );
}

function AdminMemberships() {
  const [memberships, setMemberships] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [statusFilter, setStatusFilter] = useState('');

  useEffect(() => {
    fetchMemberships();
  }, [page, statusFilter]);

  const fetchMemberships = async () => {
    try {
      const response = await adminAPI.listMemberships(page, 20, statusFilter || null);
      setMemberships(response.data.memberships);
      setTotalPages(response.data.pages);
    } catch (error) {
      console.error('Failed to fetch memberships:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      active: 'badge-success',
      canceled: 'badge-warning',
      expired: 'badge-danger',
      past_due: 'badge-danger',
    };
    return badges[status] || 'badge-primary';
  };

  return (
    <div className="admin-memberships">
      <h2>Subscriptions</h2>

      <div className="admin-toolbar">
        <select
          className="input"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          style={{ maxWidth: '200px' }}
        >
          <option value="">All Statuses</option>
          <option value="active">Active</option>
          <option value="canceled">Canceled</option>
          <option value="expired">Expired</option>
          <option value="past_due">Past Due</option>
        </select>
      </div>

      {loading ? (
        <div className="spinner" />
      ) : (
        <>
          <div className="table-container card">
            <table className="table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>User</th>
                  <th>Plan</th>
                  <th>Status</th>
                  <th>Period Start</th>
                  <th>Period End</th>
                  <th>Created</th>
                </tr>
              </thead>
              <tbody>
                {memberships.length === 0 ? (
                  <tr>
                    <td colSpan="7" style={{ textAlign: 'center', padding: '2rem' }}>
                      No memberships found
                    </td>
                  </tr>
                ) : (
                  memberships.map((mem) => (
                    <tr key={mem.id}>
                      <td>#{mem.id}</td>
                      <td>{mem.user_email || `User #${mem.user_id}`}</td>
                      <td>{mem.plan_name || '-'}</td>
                      <td>
                        <span className={`badge ${getStatusBadge(mem.status)}`}>
                          {mem.status}
                        </span>
                      </td>
                      <td>
                        {mem.current_period_start
                          ? new Date(mem.current_period_start).toLocaleDateString()
                          : '-'}
                      </td>
                      <td>
                        {mem.current_period_end
                          ? new Date(mem.current_period_end).toLocaleDateString()
                          : '-'}
                      </td>
                      <td>{new Date(mem.created_at).toLocaleString()}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="pagination">
              <button
                className="btn btn-secondary btn-sm"
                disabled={page === 1}
                onClick={() => setPage(page - 1)}
              >
                Previous
              </button>
              <span>Page {page} of {totalPages}</span>
              <button
                className="btn btn-secondary btn-sm"
                disabled={page >= totalPages}
                onClick={() => setPage(page + 1)}
              >
                Next
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function Admin() {
  const location = useLocation();

  const navItems = [
    { path: '/admin', label: 'Dashboard' },
    { path: '/admin/users', label: 'Users' },
    { path: '/admin/generations', label: 'Generations' },
    { path: '/admin/transactions', label: 'Transactions' },
    { path: '/admin/subscriptions', label: 'Subscriptions' },
    { path: '/admin/credit-packs', label: 'Credit Packs' },
    { path: '/admin/membership-plans', label: 'Plans' },
  ];

  return (
    <div className="page admin-page">
      <div className="container">
        <div className="admin-layout">
          {/* Sidebar */}
          <aside className="admin-sidebar">
            <h2 className="sidebar-title">Admin Panel</h2>
            <nav className="admin-nav">
              {navItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`admin-nav-link ${
                    location.pathname === item.path ? 'active' : ''
                  }`}
                >
                  {item.label}
                </Link>
              ))}
            </nav>
          </aside>

          {/* Content */}
          <main className="admin-content">
            <Routes>
              <Route index element={<AdminDashboard />} />
              <Route path="users" element={<AdminUsers />} />
              <Route path="generations" element={<AdminGenerations />} />
              <Route path="transactions" element={<AdminTransactions />} />
              <Route path="subscriptions" element={<AdminMemberships />} />
              <Route path="credit-packs" element={<AdminCreditPacks />} />
              <Route path="membership-plans" element={<AdminMembershipPlans />} />
            </Routes>
          </main>
        </div>
      </div>
    </div>
  );
}

export default Admin;
